import { useMemo } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from 'design-system';
import { useSearchParams } from 'react-router';

import { FACTSHEET_TAB_PARAM_NAME, TAB_NAMES, TAB_POSITIONS } from '@/features/factsheet/constants';
import { hasConfigField, hasVersionHistoryField } from '@/features/factsheet/lib/versionHistory';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import type { FactsheetDetails, FactsheetTab } from '@/features/factsheet/types';
import { cn } from '@/lib/utils';
import { StorageKeys } from '@/services/storage';
import { useStorageValue } from '@/services/storage/react';

import ApproachTab from './Tabs/ApproachTab';
import ChartTab from './Tabs/ChartTab';
import CriteriaTab from './Tabs/CriteriaTab';
import DetailsTab from './Tabs/DetailsTab';
import FlowchartTab from './Tabs/FlowchartTab';
import ItemsTab from './Tabs/ItemsTab';
import QualitySpecTab from './Tabs/QualitySpecTab';
import RelatedCodesTab from './Tabs/RelatedCodesTab';
import UnregisteredBanner from './UnregisteredBanner';

interface FactsheetBodyProps {
  factsheet: FactsheetDetails;
  openNewTab: (code: string) => void;
}

/**
 * Builds the array of tabs based on factsheet content and user authentication.
 */
function buildContentTabs(
  factsheet: FactsheetDetails,
  isAuthenticated: boolean,
  openNewTab: (code: string) => void
): FactsheetTab[] {
  const tabs: FactsheetTab[] = [
    {
      position: TAB_POSITIONS.DETAILS,
      name: TAB_NAMES.DETAILS,
      component: (
        <DetailsTab
          factsheet={factsheet}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      position: TAB_POSITIONS.APPROACH,
      name: TAB_NAMES.APPROACH,
      component: <ApproachTab factsheet={factsheet} />
    },
    {
      position: TAB_POSITIONS.FLOWCHART,
      name: TAB_NAMES.FLOWCHART,
      component: (
        <FlowchartTab
          factsheet={factsheet}
          openNewTab={openNewTab}
        />
      )
    },
    {
      position: TAB_POSITIONS.RELATED_CODES,
      name: TAB_NAMES.RELATED_CODES,
      component: (
        <RelatedCodesTab
          factsheet={factsheet}
          openNewTab={openNewTab}
        />
      )
    }
  ];

  // Conditionally add chart tab
  if (factsheet.spotSymbols.length > 0) {
    tabs.push({
      position: TAB_POSITIONS.CHART,
      name: TAB_NAMES.CHART,
      component: <ChartTab factsheet={factsheet} />
    });
  }

  // Conditionally add quality spec tab
  const shouldShowConfigTab = (
    tabName: 'qualitySpecification' | 'criteriaForInclusion' | 'calculationApproach'
  ) => {
    return hasConfigField(factsheet, tabName) || hasVersionHistoryField(factsheet, tabName);
  };
  if (shouldShowConfigTab('qualitySpecification')) {
    tabs.push({
      position: TAB_POSITIONS.QUALITY_SPEC,
      name: TAB_NAMES.QUALITY_SPEC,
      component: (
        <QualitySpecTab
          factsheet={factsheet}
          isAuthenticated={isAuthenticated}
        />
      )
    });
  }

  // Conditionally add criteria tab
  if (shouldShowConfigTab('criteriaForInclusion')) {
    tabs.push({
      position: TAB_POSITIONS.CRITERIA,
      name: TAB_NAMES.CRITERIA,
      component: <CriteriaTab factsheet={factsheet} />
    });
  }

  // Conditionally add items tab
  if (factsheet.symbols.length > 0) {
    tabs.push({
      position: TAB_POSITIONS.AVAILABLE_ITEMS,
      name: TAB_NAMES.AVAILABLE_ITEMS,
      component: <ItemsTab factsheet={factsheet} />
    });
  }

  return tabs.toSorted((a, b) => a.position - b.position);
}

const FactsheetBody = ({ factsheet, openNewTab }: FactsheetBodyProps) => {
  const gxToken = useStorageValue(StorageKeys.GxToken);
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const isCompact = useIsFactsheetCompact();

  const isAuthenticated = Boolean(gxToken);

  const contentTabs = useMemo(
    () => buildContentTabs(factsheet, isAuthenticated, openNewTab),
    [factsheet, isAuthenticated, openNewTab]
  );

  // contentTabs is always sorted by position (see buildContentTabs), so contentTabs[0] is
  // genuinely the first available tab by position — not assumed to be any specific tab name.
  // Which tab lands at position 0 varies per factsheet (e.g. Chart when spot symbols exist).
  const activeTabName = useMemo(() => {
    const activeTabParam = decodeURIComponent(urlSearchParams.get(FACTSHEET_TAB_PARAM_NAME) ?? '');
    const match = contentTabs.find(tab => tab.name === activeTabParam);
    return match?.name ?? contentTabs[0]?.name ?? '';
  }, [contentTabs, urlSearchParams]);

  const updateActiveTabParam = (tabName: string): void => {
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.set(FACTSHEET_TAB_PARAM_NAME, tabName);
    setUrlSearchParams(newSearchParams);
  };

  return (
    <div
      className={cn(
        'flex w-full flex-1 flex-grow flex-col items-start',
        isCompact ? 'overflow-auto px-0' : 'overflow-hidden px-6'
      )}
    >
      <Tabs
        className='flex w-full flex-1 flex-col'
        value={activeTabName}
        onValueChange={updateActiveTabParam}
      >
        <div
          className={cn('flex w-full justify-between', isCompact ? 'mt-2 mb-4 px-4' : 'mt-6 mb-6')}
        >
          <TabsList>
            {contentTabs.map(tab => (
              <TabsTrigger
                key={tab.name}
                value={tab.name}
              >
                {tab.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {contentTabs.map(tab => (
          <TabsContent
            key={`${factsheet.code}-${tab.name}`}
            className={cn('w-full flex-1 flex-grow overflow-auto', isCompact ? 'px-4' : 'px-0')}
            value={tab.name}
          >
            {isAuthenticated || tab.position === TAB_POSITIONS.DETAILS ? (
              tab.component
            ) : (
              <UnregisteredBanner />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default FactsheetBody;
